import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { DashboardResponse, RachaResponse, CargaTrabajoResponse, ResumenDetalladoResponse, EscuadronResponse } from '../models/dashboard.model';
import { InsigniaResponse } from '../models/insignia.model';
import { NotificacionResponse } from '../models/notificacion.model';
import { WeekRange, ModalState, TabType, TareaSemana, HistorialSemana } from '../models/ui.model';
import { getCurrentWeekRange } from './week-utils';

@Injectable({ providedIn: 'root' })
export class SelfSpaceStateService {
  // Core data
  private readonly rachaSubject = new BehaviorSubject<RachaResponse | null>(null);
  private readonly cargaTrabajoSubject = new BehaviorSubject<CargaTrabajoResponse | null>(null);
  private readonly resumenSubject = new BehaviorSubject<ResumenDetalladoResponse | null>(null);
  private readonly escuadronSubject = new BehaviorSubject<EscuadronResponse | null>(null);
  private readonly insigniasSubject = new BehaviorSubject<InsigniaResponse[]>([]);
  private readonly tareasSubject = new BehaviorSubject<TareaSemana[]>([]);
  private readonly historialSubject = new BehaviorSubject<HistorialSemana | null>(null);
  private readonly notificacionesSubject = new BehaviorSubject<NotificacionResponse[]>([]);
  private readonly notificacionesPendientesCountSubject = new BehaviorSubject<number>(0);

  // UI state
  private readonly selectedWeekSubject = new BehaviorSubject<WeekRange>(getCurrentWeekRange());
  private readonly activeTabSubject = new BehaviorSubject<TabType>('tareas');
  private readonly modalStateSubject = new BehaviorSubject<ModalState>({ open: false });
  private readonly isHistoricalSubject = new BehaviorSubject<boolean>(false);
  private readonly loadingSubject = new BehaviorSubject<boolean>(true);
  private readonly sobrecargaNotificadaSubject = new BehaviorSubject<boolean>(false);

  // Observables
  readonly racha$ = this.rachaSubject.asObservable();
  readonly cargaTrabajo$ = this.cargaTrabajoSubject.asObservable();
  readonly resumen$ = this.resumenSubject.asObservable();
  readonly escuadron$ = this.escuadronSubject.asObservable();
  readonly insignias$ = this.insigniasSubject.asObservable();
  readonly tareas$ = this.tareasSubject.asObservable();
  readonly historial$ = this.historialSubject.asObservable();
  readonly notificaciones$ = this.notificacionesSubject.asObservable();
  readonly notificacionesPendientesCount$ = this.notificacionesPendientesCountSubject.asObservable();

  readonly selectedWeek$ = this.selectedWeekSubject.asObservable();
  readonly activeTab$ = this.activeTabSubject.asObservable();
  readonly modalState$ = this.modalStateSubject.asObservable();
  readonly isHistorical$ = this.isHistoricalSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  readonly sobrecargaNotificada$ = this.sobrecargaNotificadaSubject.asObservable();

  // Derived observables
  readonly isCurrentWeek$: Observable<boolean> = this.selectedWeekSubject.pipe(
    map(week => {
      const current = getCurrentWeekRange();
      return week.startDate === current.startDate;
    }),
    distinctUntilChanged()
  );

  readonly notificacionesPendientes$: Observable<number> = this.notificacionesSubject.pipe(
    map(ns => ns.filter(n => !n.leida).length),
    distinctUntilChanged()
  );

  // Setters
  setRacha(racha: RachaResponse): void {
    this.rachaSubject.next(racha);
  }

  setCargaTrabajo(carga: CargaTrabajoResponse): void {
    this.cargaTrabajoSubject.next(carga);
  }

  setResumen(resumen: ResumenDetalladoResponse): void {
    this.resumenSubject.next(resumen);
  }

  setEscuadron(escuadron: EscuadronResponse): void {
    this.escuadronSubject.next(escuadron);
  }

  setInsignias(insignias: InsigniaResponse[]): void {
    this.insigniasSubject.next(insignias);
  }

  setTareas(tareas: TareaSemana[]): void {
    this.tareasSubject.next(tareas);
  }

  setHistorial(historial: HistorialSemana): void {
    this.historialSubject.next(historial);
  }

  setNotificaciones(notificaciones: NotificacionResponse[]): void {
    this.notificacionesSubject.next(notificaciones);
  }

  addNotificacion(notificacion: NotificacionResponse): void {
    const current = this.notificacionesSubject.getValue();
    this.notificacionesSubject.next([notificacion, ...current]);
  }

  setNotificacionesPendientesCount(count: number): void {
    this.notificacionesPendientesCountSubject.next(count);
  }

  setSelectedWeek(week: WeekRange): void {
    this.selectedWeekSubject.next(week);
  }

  setActiveTab(tab: TabType): void {
    this.activeTabSubject.next(tab);
  }

  setModalState(state: ModalState): void {
    this.modalStateSubject.next(state);
  }

  setIsHistorical(value: boolean): void {
    this.isHistoricalSubject.next(value);
  }

  setLoading(value: boolean): void {
    this.loadingSubject.next(value);
  }

  setSobrecargaNotificada(value: boolean): void {
    this.sobrecargaNotificadaSubject.next(value);
  }

  // Getters for current values
  get currentWeek(): WeekRange {
    return this.selectedWeekSubject.getValue();
  }

  get currentTab(): TabType {
    return this.activeTabSubject.getValue();
  }

  get currentModalState(): ModalState {
    return this.modalStateSubject.getValue();
  }

  get currentNotificaciones(): NotificacionResponse[] {
    return this.notificacionesSubject.getValue();
  }

  get currentInsignias(): InsigniaResponse[] {
    return this.insigniasSubject.getValue();
  }

  get currentEscuadronEquipoId(): number | undefined {
    return this.escuadronSubject.getValue()?.equipoId;
  }

  get isSobrecargaNotificada(): boolean {
    return this.sobrecargaNotificadaSubject.getValue();
  }

  get currentTareas(): TareaSemana[] {
    return this.tareasSubject.getValue();
  }

  get currentResumen(): ResumenDetalladoResponse | null {
    return this.resumenSubject.getValue();
  }

  get currentHistorial(): HistorialSemana | null {
    return this.historialSubject.getValue();
  }

  get hasHistorial(): boolean {
    return this.historialSubject.getValue() !== null;
  }

  // Reset
  resetForWeekChange(): void {
    this.loadingSubject.next(true);
    this.sobrecargaNotificadaSubject.next(false);
  }
}
